/**
 * Service Worker Cleanup Utility
 *
 * BUG FIX v32: Automatic service-worker invalidation on deploy
 * Uses build-time version from environment to detect new deployments
 * Prevents blank screens caused by cached service workers
 *
 * BUG FIX P0: Cleanup now only runs when:
 *   - In development mode (import.meta.env.DEV)
 *   - Explicit query flag ?swreset=1
 *   - Version actually changed (fixed Date.now() fallback that caused cleanup on every load)
 * BUG FIX P0: Signals update checker to stop when cleanup runs
 */

import { disableUpdateChecks } from "./serviceWorkerUpdate";

const SERVICE_WORKER_VERSION_KEY = "coretrak.sw.version";
// BUG FIX P0: Use a stable fallback instead of Date.now()
// Date.now() changes on every page load, causing cleanup to run every time
// Fallback to '1.0.0' if no build version is set — this is stable and won't trigger cleanup loops
const BUILD_VERSION =
  import.meta.env.VITE_BUILD_VERSION ||
  import.meta.env.VITE_APP_VERSION ||
  "1.0.0";
const CURRENT_SW_VERSION = BUILD_VERSION;

/**
 * Check and clean up old service workers
 * BUG FIX P0: Only runs in dev mode or with ?swreset=1 query param,
 *             OR when the build version has genuinely changed
 * Returns true if cleanup was performed
 */
export async function cleanupServiceWorkers(): Promise<{
  cleaned: boolean;
  unregistered: number;
  errors: string[];
}> {
  const errors: string[] = [];
  let cleaned = false;
  let unregistered = 0;

  if (!("serviceWorker" in navigator)) {
    return { cleaned: false, unregistered: 0, errors: [] };
  }

  // BUG FIX P0: Only allow forced cleanup in dev mode or with explicit query flag
  const isDevMode = import.meta.env.DEV;
  const hasResetFlag =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("swreset") === "1";

  try {
    // Get all service worker registrations
    const registrations = await navigator.serviceWorker.getRegistrations();

    // No registrations — nothing to clean
    if (registrations.length === 0) {
      return { cleaned: false, unregistered: 0, errors: [] };
    }

    // BUG FIX v32: Check stored service worker version
    let storedVersion: string | null = null;
    try {
      storedVersion = localStorage.getItem(SERVICE_WORKER_VERSION_KEY);
    } catch (error) {
      console.warn("[SWCleanup] Failed to read stored SW version:", error);
    }

    const versionChanged =
      storedVersion !== null && storedVersion !== String(CURRENT_SW_VERSION);
    const isFirstRun = storedVersion === null;

    // BUG FIX P0: Determine if we should clean up
    // - Dev mode or ?swreset=1: always clean up (for debugging)
    // - Production: only clean up if version genuinely changed
    // - First run (no stored version): just store the version, don't clean up
    const shouldCleanup = isDevMode || hasResetFlag ? true : versionChanged;

    if (isFirstRun) {
      // First time — just store the version, no cleanup needed
      console.log(
        "[SWCleanup] First run, storing version:",
        CURRENT_SW_VERSION,
      );
      try {
        localStorage.setItem(
          SERVICE_WORKER_VERSION_KEY,
          String(CURRENT_SW_VERSION),
        );
      } catch (error) {
        console.warn("[SWCleanup] Failed to store SW version:", error);
      }
      return { cleaned: false, unregistered: 0, errors: [] };
    }

    if (!shouldCleanup && registrations.length <= 1) {
      // Version hasn't changed, single registration — all good
      console.log("[SWCleanup] Version unchanged, no cleanup needed");
      return { cleaned: false, unregistered: 0, errors: [] };
    }

    if (versionChanged) {
      console.log(
        `[SWCleanup] Service worker version changed: ${storedVersion} → ${CURRENT_SW_VERSION}`,
      );
    }

    if (isDevMode || hasResetFlag) {
      console.log(
        `[SWCleanup] Forced cleanup (dev=${isDevMode}, swreset=${hasResetFlag})`,
      );
    }

    // BUG FIX P0: Signal update checker to stop — prevents update checks on dead registrations
    disableUpdateChecks();

    // Unregister service workers
    for (const registration of registrations) {
      try {
        console.log(
          "[SWCleanup] Unregistering service worker:",
          registration.scope,
        );

        const unregisteredResult = await registration.unregister();

        if (unregisteredResult) {
          unregistered++;
          cleaned = true;
          console.log(
            "[SWCleanup] Successfully unregistered service worker:",
            registration.scope,
          );
        } else {
          console.warn(
            "[SWCleanup] Service worker unregistration returned false:",
            registration.scope,
          );
          errors.push(`Failed to unregister SW at ${registration.scope}`);
        }
      } catch (error) {
        console.error("[SWCleanup] Error unregistering service worker:", error);
        errors.push(
          `Error unregistering SW: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    }

    // Update stored version
    try {
      localStorage.setItem(
        SERVICE_WORKER_VERSION_KEY,
        String(CURRENT_SW_VERSION),
      );
      console.log(
        "[SWCleanup] Updated service worker version to:",
        CURRENT_SW_VERSION,
      );
    } catch (error) {
      console.warn("[SWCleanup] Failed to update SW version:", error);
    }

    console.log(
      `[SWCleanup] Cleanup complete: ${unregistered} worker(s) unregistered, ${errors.length} error(s)`,
    );
  } catch (error) {
    console.error("[SWCleanup] Service worker cleanup failed:", error);
    errors.push(
      `Cleanup failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }

  return { cleaned, unregistered, errors };
}

/**
 * Initialize service worker version on first run
 * BUG FIX v32: Always check and update version to detect new deployments
 */
export function initializeServiceWorkerVersion(): void {
  try {
    const stored = localStorage.getItem(SERVICE_WORKER_VERSION_KEY);
    const currentVersion = String(CURRENT_SW_VERSION);

    if (!stored || stored !== currentVersion) {
      localStorage.setItem(SERVICE_WORKER_VERSION_KEY, currentVersion);
      if (!stored) {
        console.log(
          "[SWCleanup] Initialized service worker version:",
          currentVersion,
        );
      } else {
        console.log(
          "[SWCleanup] Updated service worker version:",
          stored,
          "→",
          currentVersion,
        );
      }
    }
  } catch (error) {
    console.error(
      "[SWCleanup] Failed to initialize service worker version:",
      error,
    );
    // Don't throw - app can continue without version tracking
  }
}
